// Developer → API tokens. Mint / revoke Personal Access Tokens that authorize
// the GathrXR MCP server. The raw token is generated + hashed in the browser;
// only the SHA-256 hash is stored, and the raw value is shown exactly once.

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import PageHeader from '../components/PageHeader';
import './DeveloperPage.css';

interface TokenRow {
  id: string;
  name: string;
  token_prefix: string;
  last_used_at: string | null;
  created_at: string;
}

async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function newRawToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `gxr_pat_${hex}`;
}

const MCP_URL = `${typeof window !== 'undefined' ? window.location.origin : 'https://gathrxr.com'}/api/mcp`;

// What the MCP server can do (functions/api/mcp/_tools.ts). Keep in step with that file.
const TOOLS: { group: string; items: [string, string][] }[] = [
  { group: 'Account & worlds', items: [
    ['whoami', 'Your account, and whether it can use the generate tools.'],
    ['list_worlds', 'Your worlds, most recently updated first.'],
    ['create_world', 'Create an empty world; returns its id and links.'],
    ['get_world', 'A world’s settings and every object in it.'],
    ['update_world_settings', 'Name, public/private, spawn point, atmosphere, ground.'],
    ['delete_world', 'Permanently delete a world (owner only).'],
  ] },
  { group: 'Building', items: [
    ['apply_world_plan', 'Create or fully replace a world from a scene plan in one call — the main building tool.'],
    ['list_assets', 'Every object in a world.'],
    ['add_primitive', 'Box, sphere, cylinder, cone, plane, torus or text, with colours or built-in textures.'],
    ['add_model', 'A GLB/GLTF model from a URL.'],
    ['add_light', 'Point, spot or area light.'],
    ['set_skybox', 'Set the 360° sky from an image URL.'],
    ['delete_asset', 'Remove an object by id.'],
  ] },
  { group: 'Models & skies', items: [
    ['search_models', 'Search the free Poly Pizza model library (CC0 / CC-BY).'],
    ['generate_3d_model', 'Start a text-to-3D model (uses your AI allowance).'],
    ['refine_3d_model', 'Add textures to a finished preview model (uses your AI allowance).'],
    ['generate_skybox', 'Start a 360° Skybox AI sky from a prompt, with an optional style and quality (Studio credits: standard 1, enhanced 2).'],
    ['get_generation', 'Check a generation job and get its result.'],
  ] },
];

const ConnectGuide: React.FC<{ signedIn: boolean }> = ({ signedIn }) => (
  <>
    <section className="dev-card">
      <h2>Connect an AI assistant</h2>
      <p className="dev-muted">GathrXR runs an MCP server, so assistants like Claude can build worlds for you from a chat — scoped to exactly what your account can do.</p>
      <div className="dev-kv"><span>Endpoint</span><code>{MCP_URL}</code></div>

      <h3>claude.ai (custom connector)</h3>
      <ol className="dev-steps">
        <li>In claude.ai, open <strong>Settings → Connectors</strong> and choose <strong>Add custom connector</strong>.</li>
        <li>Name it <code>GathrXR</code> and paste the endpoint above as the URL.</li>
        <li>Click <strong>Connect</strong>, sign in to GathrXR when asked, and allow access. No token needed — you can disconnect it any time.</li>
      </ol>

      <h3>Claude Code</h3>
      <pre className="dev-pre">{`claude mcp add --transport http gathrxr ${MCP_URL} \\
  --header "Authorization: Bearer gxr_pat_YOUR_TOKEN"`}</pre>
      <h3>Other MCP clients (HTTP config)</h3>
      <pre className="dev-pre">{`{
  "mcpServers": {
    "gathrxr": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer gxr_pat_YOUR_TOKEN" }
    }
  }
}`}</pre>
      <p className="dev-muted dev-small">
        Claude Code and other clients use a personal API token as a bearer header{signedIn ? ' — create one in the API keys tab.' : ' — sign in to create one.'}
      </p>
      <p className="dev-muted dev-small dev-try">Try: “List my worlds”, then “Build a small neon-lit plaza with a fountain and four benches.” For better results, give your assistant the guide in the <strong>AI prompt</strong> tab.</p>
    </section>
  </>
);

const ToolsTab: React.FC = () => (
  <>
    <section className="dev-card">
      <h2>Tools</h2>
      <p className="dev-muted">Everything the GathrXR MCP server exposes.</p>
      {TOOLS.map((g) => (
        <div key={g.group} className="dev-tools">
          <h3>{g.group}</h3>
          <ul>
            {g.items.map(([n, d]) => <li key={n}><code>{n}</code><span>{d}</span></li>)}
          </ul>
        </div>
      ))}
    </section>
  </>
);

// The assistant guide lives in public/llms.txt (one source: assistants can
// fetch it by URL, and this tab shows the same text to copy).
const GUIDE_URL = `${typeof window !== 'undefined' ? window.location.origin : 'https://gathrxr.com'}/llms.txt`;
const SHORT_PROMPT = `You can build and manage my GathrXR 3D/VR worlds with the GathrXR MCP tools. Before building, read the guide at ${GUIDE_URL} and follow it: units, sizes, how to plan a scene, and which tools are safe. Ask before deleting or replacing anything, keep new worlds private unless I say otherwise, and finish by giving me the build and play links.`;

const PromptTab: React.FC = () => {
  const [guide, setGuide] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    fetch('/llms.txt').then((r) => (r.ok ? r.text() : Promise.reject())).then(setGuide).catch(() => setGuide(''));
  }, []);
  const copy = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  return (
    <>
      <section className="dev-card">
        <h2>Prompt for your assistant</h2>
        <p className="dev-muted">Paste this into your assistant's instructions so it knows how GathrXR works: in claude.ai, a Project's <strong>instructions</strong>; in Claude Code, your <code>CLAUDE.md</code>; in ChatGPT, <strong>Custom instructions</strong>. It points to the full guide below, which assistants that can open links will read.</p>
        <pre className="dev-pre dev-pre-wrap">{SHORT_PROMPT}</pre>
        <button className="dev-btn dev-btn-primary" onClick={() => copy('short', SHORT_PROMPT)}>{copied === 'short' ? 'Copied' : 'Copy prompt'}</button>
      </section>
      <section className="dev-card">
        <div className="dev-card-head">
          <h2>Full guide</h2>
          <div className="dev-head-links">
            <a className="dev-link" href="/llms.txt" target="_blank" rel="noreferrer">Open llms.txt</a>
            <button className="dev-btn" onClick={() => guide && copy('guide', guide)} disabled={!guide}>{copied === 'guide' ? 'Copied' : 'Copy guide'}</button>
          </div>
        </div>
        <p className="dev-muted">Everything an assistant needs: workflow, units and real-world sizes, every object type, lights, atmosphere, textures and an example. Paste the whole guide instead of the short prompt if your assistant can't open links.</p>
        <pre className="dev-pre dev-pre-wrap dev-guide">{guide === null ? 'Loading…' : guide || 'Could not load the guide.'}</pre>
      </section>
    </>
  );
};

type Tab = 'connect' | 'keys' | 'prompt' | 'tools';
const TABS: { id: Tab; label: string }[] = [
  { id: 'connect', label: 'Connect' },
  { id: 'keys', label: 'API keys' },
  { id: 'prompt', label: 'AI prompt' },
  { id: 'tools', label: 'Tools' },
];
const tabFromHash = (): Tab => {
  const h = (typeof window !== 'undefined' ? window.location.hash.slice(1) : '') as Tab;
  return TABS.some((t) => t.id === h) ? h : 'connect';
};

const TabBar: React.FC<{ tab: Tab; onChange: (t: Tab) => void }> = ({ tab, onChange }) => (
  <div className="dev-tabs" role="tablist" aria-label="Developer sections">
    {TABS.map((t) => (
      <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => onChange(t.id)}>{t.label}</button>
    ))}
  </div>
);

const DeveloperPage: React.FC = () => {
  const [tab, setTabState] = useState<Tab>(tabFromHash);
  const setTab = (t: Tab) => { setTabState(t); window.history.replaceState(null, '', `#${t}`); };
  const [userId, setUserId] = useState<string | null>(null);
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('MCP token');
  const [creating, setCreating] = useState(false);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadTokens = useCallback(async (uid: string) => {
    const { data, error } = await supabase
      .from('api_tokens')
      .select('id,name,token_prefix,last_used_at,created_at')
      .is('revoked_at', null)
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else setTokens((data as TokenRow[]) || []);
    setLoading(false);
    void uid;
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id || null;
      setUserId(uid);
      if (uid) loadTokens(uid);
      else setLoading(false);
    });
  }, [loadTokens]);

  const createToken = async () => {
    if (!userId) return;
    setCreating(true);
    setError(null);
    try {
      const raw = newRawToken();
      const token_hash = await sha256Hex(raw);
      const token_prefix = raw.slice(0, 16);
      const { error } = await supabase.from('api_tokens').insert({
        user_id: userId, name: name.trim() || 'MCP token', token_hash, token_prefix,
      });
      if (error) throw error;
      setFreshToken(raw);
      setName('MCP token');
      await loadTokens(userId);
    } catch (e: any) {
      setError(e?.message || 'Could not create token');
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (id: string) => {
    if (!userId) return;
    const { error } = await supabase.from('api_tokens').update({ revoked_at: new Date().toISOString() }).eq('id', id);
    if (error) setError(error.message);
    else await loadTokens(userId);
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return <div className="dev-page"><PageHeader title="Developers" accent="Build from a chat." /><div className="dev-shell ph-container"><p className="dev-muted">Loading…</p></div></div>;
  }

  if (!userId) {
    return (
      <div className="dev-page">
        <PageHeader
          title="Developers"
          accent="Build from a chat."
          subtitle="Connect Claude or another AI assistant to GathrXR. Sign in to create API keys."
        />
        <div className="dev-shell ph-container">
          <TabBar tab={tab} onChange={setTab} />
          {tab === 'connect' && <ConnectGuide signedIn={false} />}
          {tab === 'prompt' && <PromptTab />}
          {tab === 'tools' && <ToolsTab />}
          {tab === 'keys' && (
            <section className="dev-card">
              <h2>API keys</h2>
              <p className="dev-muted">Sign in to create and manage your API keys.</p>
            </section>
          )}
          <Link className="dev-btn" to="/login?next=%2Fdeveloper">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dev-page">
      <PageHeader
        title="Developers"
        accent="Build from a chat."
        subtitle="Connect Claude or another AI assistant to build worlds for you. API keys let the GathrXR MCP server act for you, limited to what your account can do."
      />
      <div className="dev-shell ph-container">

        <TabBar tab={tab} onChange={setTab} />

        {error && <div className="dev-error">{error}</div>}

        {freshToken && (
          <div className="dev-fresh">
            <div className="dev-fresh-head">
              <strong>Copy your token now — it won't be shown again.</strong>
              <button className="dev-x" onClick={() => setFreshToken(null)}>×</button>
            </div>
            <div className="dev-token-row">
              <code>{freshToken}</code>
              <button className="dev-btn" onClick={() => copy(freshToken)}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
          </div>
        )}

        {tab === 'keys' && (<>
        <section className="dev-card">
          <h2>Create a token</h2>
          <div className="dev-create">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Token name (e.g. Claude Code)" maxLength={60} />
            <button className="dev-btn dev-btn-primary" onClick={createToken} disabled={creating}>{creating ? 'Creating…' : 'Create token'}</button>
          </div>
        </section>

        <section className="dev-card">
          <h2>Your tokens</h2>
          {tokens.length === 0 ? (
            <p className="dev-muted">No active tokens yet.</p>
          ) : (
            <ul className="dev-list">
              {tokens.map((t) => (
                <li key={t.id}>
                  <div>
                    <div className="dev-token-name">{t.name}</div>
                    <div className="dev-muted dev-small">
                      <code>{t.token_prefix}…</code> · created {new Date(t.created_at).toLocaleDateString()} ·
                      {t.last_used_at ? ` last used ${new Date(t.last_used_at).toLocaleDateString()}` : ' never used'}
                    </div>
                  </div>
                  <button className="dev-btn dev-btn-danger" onClick={() => revokeToken(t.id)}>Revoke</button>
                </li>
              ))}
            </ul>
          )}
        </section>
        </>)}

        {tab === 'connect' && <ConnectGuide signedIn />}
        {tab === 'prompt' && <PromptTab />}
        {tab === 'tools' && <ToolsTab />}
      </div>
    </div>
  );
};

export default DeveloperPage;
